import { useState } from 'react';
import type { PolicyTab } from '../../../content/policiesContent';
import { useAuth } from './AuthContext';
import {
  AuthButton,
  AuthError,
  AuthInput,
  AuthPageShell,
  AuthPolicyFooter,
} from './AuthPageLayout';
import { AUTH_GREEN, AUTH_MUTED } from './authTheme';
import PoliciesTermsModal from './PoliciesTermsModal';

const REMEMBER_EMAIL_KEY = 'digisaka.rememberEmail';

interface LoginProps {
  onSwitchToSignup: () => void;
  onForgotPassword: () => void;
}

export default function Login({ onSwitchToSignup, onForgotPassword }: LoginProps) {
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_EMAIL_KEY) ?? '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => Boolean(localStorage.getItem(REMEMBER_EMAIL_KEY)));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const [policiesOpen, setPoliciesOpen] = useState(false);
  const [policiesTab, setPoliciesTab] = useState<PolicyTab>('privacy');

  const openPolicies = (tab: PolicyTab) => {
    setPoliciesTab(tab);
    setPoliciesOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (rememberMe) localStorage.setItem(REMEMBER_EMAIL_KEY, email.trim());
      else localStorage.removeItem(REMEMBER_EMAIL_KEY);
      await login(email, password);
    } catch (err: any) {
      console.error('Login error:', err);

      if (!err.response) {
        setError('Network error. Please check your internet connection and try again.');
        return;
      }

      const errorDetail = err.response?.data?.detail;
      if (errorDetail === 'Please verify your email before logging in') {
        setError('Please verify your email before logging in. Check your inbox for the verification code.');
      } else if (errorDetail === 'Incorrect email or password') {
        setError('Incorrect email/mobile or password. Please try again.');
      } else if (
        errorDetail === 'Invalid refresh token' ||
        errorDetail === 'Not authenticated' ||
        errorDetail === 'Could not validate credentials'
      ) {
        setError(
          'Sign-in succeeded but the browser did not keep your session. Restart the dev server, open http://127.0.0.1:3000 (not localhost), and leave VITE_DIGISAKA_API_ORIGIN empty in .env so the API is proxied.'
        );
      } else {
        setError(errorDetail || 'Failed to login. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PoliciesTermsModal
        isOpen={policiesOpen}
        initialTab={policiesTab}
        onClose={() => setPoliciesOpen(false)}
      />

      <AuthPageShell
        title="Welcome back"
        subtitle="Sign in to manage your agricultural activities."
        autoAdvanceMs={5000}
        footer={
          <AuthPolicyFooter
            switchPrompt="Don't have an account?"
            switchLabel="Create For Free"
            onSwitch={onSwitchToSignup}
            onOpenPrivacy={() => openPolicies('privacy')}
            onOpenTerms={() => openPolicies('terms')}
          />
        }
      >
        {error && <AuthError message={error} />}

        <form onSubmit={handleSubmit} className="space-y-5">
          <AuthInput
            id="email"
            label="Email or mobile"
            type="text"
            icon="ri-mail-line"
            value={email}
            onChange={setEmail}
            placeholder="you@example.com or 09171234567"
            autoComplete="username"
            required
          />

          <div>
            <AuthInput
              id="password"
              label="Password"
              type="password"
              icon="ri-lock-2-line"
              value={password}
              onChange={setPassword}
              placeholder="Enter your password"
              autoComplete="current-password"
              passwordToggle
              required
            />
            <div className="mt-2 flex items-center justify-between gap-3">
              <label
                className="inline-flex cursor-pointer items-center gap-2 text-xs"
                style={{ color: AUTH_MUTED }}
              >
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-[#008540] focus:ring-[#008540]"
                />
                Remember me
              </label>
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-xs font-semibold transition hover:underline"
                style={{ color: AUTH_GREEN }}
              >
                Forgot password?
              </button>
            </div>
          </div>

          <AuthButton type="submit" loading={loading}>
            {loading ? 'Signing in…' : 'Login'}
          </AuthButton>
        </form>

        <p className="mt-3 text-center text-[11px]" style={{ color: AUTH_MUTED }}>
          Signing in uses essential session cookies. See our{' '}
          <button
            type="button"
            onClick={() => openPolicies('privacy')}
            className="font-medium hover:underline"
            style={{ color: AUTH_GREEN }}
          >
            Privacy Policy
          </button>
          .
        </p>
      </AuthPageShell>
    </>
  );
}
